var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getDareDropApiUrl } from 'root/src/client/v2/common/api/getDareDropApiUrl';
import { getCookie } from 'root/src/client/v2/browser/helpers/cookies';
// The gateway envelope's `error` is `{ code, message, ... }` where `message` is usually a string,
// but for a PayloadSchemaError it is a (possibly nested) field -> message map built by ajvErrors.
// Walk it for human-readable leaves so schema rejections (e.g. an overlong share message) surface
// as the actual validation message instead of a generic "Request failed".
// ajv keywords with no ajvErrors mapping arrive as a stringified raw ajv error; recover the field
// name (from dataPath) plus ajv's own human message ("should NOT be longer than 200 characters").
const parseRawAjvLeaf = (leaf) => {
    try {
        const parsed = JSON.parse(leaf);
        if (!parsed || typeof parsed !== 'object') {
            return undefined;
        }
        const field = typeof parsed.dataPath === 'string' ? parsed.dataPath.split('/').filter(Boolean).pop() : undefined;
        if (parsed.message) {
            return field ? `${field}: ${parsed.message}` : parsed.message;
        }
        return field && parsed.keyword ? `${field}: failed ${parsed.keyword} validation` : undefined;
    }
    catch (_a) {
        return undefined;
    }
};
const collectMessageLeaves = (value, path = [], acc = []) => {
    if (typeof value === 'string') {
        const readable = value.startsWith('{') || value.startsWith('[') ? parseRawAjvLeaf(value) : value;
        if (readable) {
            acc.push({ path, text: readable });
        }
        return acc;
    }
    if (value && typeof value === 'object') {
        for (const [key, nested] of Object.entries(value)) {
            collectMessageLeaves(nested, [...path, key], acc);
        }
    }
    return acc;
};
// "Dare 2: Description maximum 110 characters" instead of an anonymous field error: a campaign can
// carry a dozen dares, and the error path (dares/<index>/...) is the only pointer to the culprit.
const labelForPath = (path) => {
    const daresAt = path.indexOf('dares');
    const index = daresAt >= 0 ? Number(path[daresAt + 1]) : NaN;
    return Number.isInteger(index) ? `Dare ${index + 1}: ` : '';
};
const extractErrorMessage = (error) => {
    if (typeof error === 'string') {
        return error;
    }
    if (error && typeof error === 'object') {
        const { message, code } = error;
        const leaves = collectMessageLeaves(message);
        const [first] = leaves;
        if (first) {
            const text = `${labelForPath(first.path)}${first.text}`;
            return leaves.length > 1 ? `${text} (+${leaves.length - 1} more)` : text;
        }
        // Nothing readable in the payload; at least name the error class instead of a bare fallback.
        return typeof code === 'string' && code ? `Request failed (${code})` : undefined;
    }
    return undefined;
};
// Redesign-local DareDrop API caller. Mirrors `useDareDropApi`'s controlled fetcher (same envelope,
// same cookie auth) but sends an explicit `Content-Type: application/json` header. The shared
// fetcher omits the header, so its requests are sent as `text/plain`; the local API does not parse
// those bodies and 302-redirects, which the browser surfaces as a CORS "Failed to fetch". We cannot
// touch the shared fetcher (legacy A/B), so the redesign uses this for its REST reads. Standard
// `application/json` works against both the local and deployed API.
export function redesignApiFetch(endpointId, payload) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield fetch(getDareDropApiUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                payload,
                endpointId,
                authentication: getCookie('accessToken'),
            }),
        });
        const json = yield response.json();
        if (json.statusCode < 200 || json.statusCode >= 300) {
            // Keep the raw envelope error on `cause` (same pattern as dareDropClient) for debugging.
            throw new Error((_a = extractErrorMessage(json.error)) !== null && _a !== void 0 ? _a : 'Request failed', { cause: json.error });
        }
        return json.body;
    });
}
